import {
  Box,
  Chip,
  Divider,
  Icon,
  Theme,
  Typography
} from "@outschool/backpack";
import { ActivityEnrollQueryQuery } from "@outschool/gql-frontend-generated";
import { faGlobe } from "@outschool/icons";
import { Trans, useLocale, useTranslation } from "@outschool/localization";
import {
  dayjs,
  getDaysOfWeek,
  startEndTime,
  startOfIsoWeekUtc,
  timeZoneName
} from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { TrackedButton } from "@outschool/ui-components-shared";
import { AvailableDateSelector } from "@outschool/ui-components-website";
import { groupBy, sortBy, startCase } from "lodash";
import React from "react";

import { useTimeZone } from "../../../providers/TimeZoneProvider";

type EnrollmentFormType = ActivityEnrollQueryQuery["enrollmentForm"];
type SectionType = NonNullable<EnrollmentFormType>["section"];

/**
 * The maximum number of future weeks that can be bookable for an ongoing section.
 * Classes with this feature enabled can be booked for either the current week or any future week up to this number.
 */
export const MAX_BOOKABLE_FUTURE_WEEKS = 4;

export default function ChooseOngoingChooseStartTime({
  section,
  onStartTimeSelected
}: {
  section: SectionType;
  onStartTimeSelected: (date: Date | null) => void;
}) {
  const trackEvent = useTrackEvent();

  const { t } = useTranslation("client\\components\\enrolling\\enrollmentForm");
  const locale = useLocale();
  const { timeZone } = useTimeZone();
  const startOfDateString = React.useCallback(
    (date: Date) => dayjs(date).tz(timeZone).startOf("date").toISOString(),
    [timeZone]
  );

  const availableStartTimes = React.useMemo(
    () =>
      Object.values(
        groupBy(section.meetings, m =>
          startOfIsoWeekUtc(m.start_time).toISOString()
        )
      )
        .map(group =>
          startOfDateString(
            dayjs(sortBy(group, m => m.start_time)[0].start_time).toDate()
          )
        )
        .slice(0, MAX_BOOKABLE_FUTURE_WEEKS + 1),
    [section.meetings, startOfDateString]
  );

  const availableStartTimesCount = availableStartTimes.length;
  const firstAvailableStartTime = availableStartTimes[0];
  const lastAvailableStartTime =
    availableStartTimes[availableStartTimes.length - 1];

  const trackingParams = React.useMemo(
    () => ({
      sectionUid: section.uid,
      availableStartTimesCount,
      firstAvailableStartTime,
      lastAvailableStartTime
    }),
    [
      section.uid,
      availableStartTimesCount,
      firstAvailableStartTime,
      lastAvailableStartTime
    ]
  );

  const [selectedDate, setSelectedDate] = React.useState<Date>(
    dayjs(availableStartTimes[0]).toDate()
  );

  const handleDayClick = React.useCallback(
    (date: Date) => {
      trackEvent("Choose Ongoing Start Time Day Clicked", {
        selectedDate: date,
        ...trackingParams
      });
      setSelectedDate(date);
    },
    [trackEvent, trackingParams]
  );

  const handleStartTimeSelected = React.useCallback(() => {
    const isStartingNow =
      startOfDateString(selectedDate) === availableStartTimes[0];
    onStartTimeSelected(isStartingNow ? null : startOfIsoWeekUtc(selectedDate));
  }, [
    onStartTimeSelected,
    selectedDate,
    availableStartTimes,
    startOfDateString
  ]);

  const meetingDays = getDaysOfWeek({
    dateTimes: section.meetings.map(m => m.start_time),
    isOneTime: false,
    timeZone,
    locale
  });

  const formattedSectionStartTime = startEndTime(
    section.meetings[0]?.start_time,
    section.meetings[0]?.end_time,
    timeZone,
    false
  );

  const hasAlreadyTracked = React.useRef(false);

  React.useEffect(() => {
    if (!hasAlreadyTracked.current) {
      hasAlreadyTracked.current = true;
      trackEvent("Choose Ongoing Start Time Viewed", {
        ...trackingParams
      });
    }
  }, [trackingParams, trackEvent, hasAlreadyTracked]);

  return (
    <Box>
      <Box>
        <Typography variant="h4" sx={{ marginTop: 12, marginBottom: 40 }}>
          {t("Confirm your start date")}
        </Typography>
      </Box>
      <Box>
        <Box
          flex
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" sx={{ marginY: 14 }}>
            {t("Choose the start date for your class")}
          </Typography>
          <Box>
            <Chip
              label={startCase(timeZoneName(timeZone))}
              size="small"
              icon={<Icon icon={faGlobe} />}
              sx={{
                alignSelf: "flex-start",
                textAlign: "center"
              }}
            />
          </Box>
        </Box>

        <Box
          flex
          sx={(theme: Theme) => ({
            justifyContent: "space-between",
            [theme.breakpoints.down("md")]: {
              flexDirection: "column",
              gap: 10
            }
          })}
        >
          <AvailableDateSelector
            availableDates={availableStartTimes}
            today={dayjs().tz(timeZone).toDate()}
            selectedDate={selectedDate}
            timeZone={timeZone}
            startOfDateString={startOfDateString}
            handleDayClick={handleDayClick}
          />
          <Box sx={{ maxWidth: 258 }}>
            <Box>
              <Trans t={t}>
                This class meets weekly <b>{{ formattedSectionStartTime }}</b>{" "}
                on:
              </Trans>
              <ul
                style={{
                  paddingLeft: "2rem",
                  marginTop: 0,
                  marginBottom: "2rem"
                }}
              >
                {meetingDays.split(", ").map(day => (
                  <li key={day}>{day}</li>
                ))}
              </ul>
            </Box>
            <Box>
              {t("New learners join on the first day of the weekly section.")}
            </Box>
          </Box>
        </Box>
        <Divider
          sx={(theme: Theme) => ({
            marginTop: 40,
            [theme.breakpoints.down("md")]: { marginTop: 10 }
          })}
        />
        <Box
          flex
          sx={(theme: Theme) => ({
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 20,
            [theme.breakpoints.down("md")]: {
              marginTop: 10,
              flexDirection: "column",
              alignItems: "stretch",
              gap: 10
            }
          })}
        >
          <Typography variant="h6">
            {t("{{meetingDays}} from {{formattedSectionStartTime}}", {
              meetingDays,
              formattedSectionStartTime
            })}
          </Typography>
          <TrackedButton
            color="primary"
            variant="contained"
            size="large"
            onClick={handleStartTimeSelected}
            trackingName="Choose Ongoing Start Time Next Clicked"
            trackingSharedProperties={{
              ...trackingParams,
              isEarlyEnrollment:
                startOfDateString(selectedDate) !== availableStartTimes[0]
            }}
          >
            {t("Next")}
          </TrackedButton>
        </Box>
      </Box>
    </Box>
  );
}
